/**
 * User Card Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Card displaying individual user information with actions.
 */

import React from 'react';
import { MoreVertical, Mail, UserCog, UserX, UserCheck, Trash2, Key, Users } from 'lucide-react';
import type { UserListItem } from '../../../types/admin';
import { ROLE_LABELS } from '../../../types/admin';
import UserStatusBadge from './UserStatusBadge';

interface UserCardProps {
  user: UserListItem;
  onEdit: (user: UserListItem) => void;
  onChangeRole: (user: UserListItem) => void;
  onToggleStatus: (user: UserListItem) => void;
  onDelete: (user: UserListItem) => void;
  onResendInvite?: (user: UserListItem) => void;
  onResetPassword?: (user: UserListItem) => void;
  onManageClients?: (user: UserListItem) => void;
  currentUserId?: string;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onChangeRole,
  onToggleStatus,
  onDelete,
  onResendInvite,
  onResetPassword,
  onManageClients,
  currentUserId,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const isCurrentUser = currentUserId === user.id;
  const isPending = user.invitation_status === 'invited' || user.invitation_status === 'pending';

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.display_name) {
      return user.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user.email[0].toUpperCase();
  };

  const formatLastLogin = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        {/* User info */}
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-[#002B45] text-white flex items-center justify-center text-sm font-medium">
            {getInitials()}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900">
                {user.display_name || user.email}
              </h3>
              <UserStatusBadge
                isActive={user.is_active}
                invitationStatus={user.invitation_status}
                size="sm"
              />
            </div>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>

        {/* Actions menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <MoreVertical size={18} />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
              <button
                onClick={() => {
                  onEdit(user);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <UserCog size={16} />
                Edit Profile
              </button>

              <button
                onClick={() => {
                  onChangeRole(user);
                  setShowMenu(false);
                }}
                disabled={isCurrentUser}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserCog size={16} />
                Change Role
              </button>

              {onManageClients && (
                <button
                  onClick={() => {
                    onManageClients(user);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Users size={16} />
                  Manage Clients
                </button>
              )}

              {isPending && onResendInvite && (
                <button
                  onClick={() => {
                    onResendInvite(user);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Mail size={16} />
                  Resend Invite
                </button>
              )}

              {!isPending && onResetPassword && (
                <button
                  onClick={() => {
                    onResetPassword(user);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Key size={16} />
                  Reset Password
                </button>
              )}

              <div className="border-t border-gray-100 my-1" />

              <button
                onClick={() => {
                  onToggleStatus(user);
                  setShowMenu(false);
                }}
                disabled={isCurrentUser}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {user.is_active ? (
                  <>
                    <UserX size={16} />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck size={16} />
                    Activate
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  onDelete(user);
                  setShowMenu(false);
                }}
                disabled={isCurrentUser}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={16} />
                Delete User
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer info */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
        <span className="text-gray-500">
          Role: <span className="font-medium text-gray-700">{ROLE_LABELS[user.role]}</span>
        </span>
        <span className="text-gray-400">
          Last login: {formatLastLogin(user.last_login_at)}
        </span>
      </div>
    </div>
  );
};

export default UserCard;
